// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T2)
import { useContext, useMemo, useState } from 'react';
import Modal from '../../../common/Modal';
import DashboardPersonaFilter from '../../../dashboard/DashboardPersonaFilter';
import { LiveReportCrudContext } from '../context/LiveReportCrudContext';
import { NO_REPORT_PERSONA_LABEL } from '../../../../helpers/liveReportHelpers/describeReportPersona';
import personaOptionDetail from '../../../../helpers/personas/personaOptionDetail';
import useStatusDisplays from '../../../../hooks/useStatusDisplay';

/**
 * @typedef {object} SelectablePersona
 * @property {string} ID - `Personas.ID`
 * @property {string} Name
 * @property {string} [Status] - 'active' | 'inactive' | 'outflow'; missing = active
 * @property {*} [StartDate] - 'yyyy-mm-dd' or Timestamp; often missing
 */

/**
 * Kiest de ene deelnemer waar een rapport over gaat (`PersonaID` op het
 * `LiveReports2`-doc). Kiezen gebeurt in een concept; pas 'Opslaan' schrijft.
 * Een rapport met een deelnemer krijgt eerst een toegangscode en pas daarna de
 * deelnemer, zodat er nooit een deelnemer op een open link staat — ook niet als
 * het aanmaken van de code mislukt.
 *
 * Het venster wordt per opening opnieuw gemount, dus het concept begint altijd
 * bij wat er opgeslagen is.
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Whether the modal is shown
 * @param {(open: boolean) => void} props.setModalOpen - Closes the modal (called with `false`)
 * @param {import('../../../../types/LiveReports2').LiveReports2Doc} props.report - Live report doc (re-derived from the list by the parent)
 * @param {SelectablePersona[] | undefined | null} props.personas - Deelnemers van de organisatie; `undefined`/`null` = nog niet geladen
 */
const ReportPersonaSelector = ({ modalOpen, setModalOpen, report, personas }) => {
    const crudContext = useContext(LiveReportCrudContext);
    if (!crudContext) {
        throw new Error('LiveReportCrudContext not found.');
    }
    const { generateShareCode, updateReport } = crudContext;

    const savedPersonaId = report.PersonaID || '';
    const [draftPersonaId, setDraftPersonaId] = useState(savedPersonaId);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(/** @type {string|null} */ (null));
    const statusLabels = useStatusDisplays().persona.labels;

    // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
    const loaded = Array.isArray(personas);
    const sortedPersonas = useMemo(
        () => (Array.isArray(personas)
            ? [...personas].sort((a, b) => String(a.Name || '').localeCompare(String(b.Name || '')))
            : []),
        [personas]
    );
    const draftPersona = loaded ? personas.find((p) => p.ID === draftPersonaId) : undefined;
    const savedMissing = loaded && savedPersonaId !== '' && !personas.some((p) => p.ID === savedPersonaId);

    const save = async () => {
        if (draftPersonaId === savedPersonaId) {
            setModalOpen(false);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            if (draftPersonaId && !report.ShareCode) {
                await generateShareCode(report.ID);
            }
            await updateReport(report, { PersonaID: draftPersonaId || null });
            setModalOpen(false);
        } catch (e) {
            console.error('ReportPersonaSelector save failed:', e);
            setError(e.message || 'Kon de deelnemer niet opslaan.');
            setBusy(false);
        }
    };

    const content = (
        <div className="report-persona-selector">
            {!loaded && <p>Deelnemers worden opgehaald…</p>}
            {loaded && personas.length === 0 && <p>Deze organisatie heeft nog geen deelnemers.</p>}
            {loaded && personas.length > 0 && (
                <div className="report-persona-selector-filters">
                    <DashboardPersonaFilter
                        personas={sortedPersonas}
                        selectedPersonaId={draftPersonaId}
                        onChange={setDraftPersonaId}
                        label="Deelnemer"
                        allLabel={NO_REPORT_PERSONA_LABEL}
                        optionDetail={(p) => personaOptionDetail(p, statusLabels)}
                    />
                </div>
            )}
            {savedMissing && draftPersonaId === savedPersonaId && (
                <p className="share-report-error">
                    De gekozen deelnemer bestaat niet meer. Kies een andere deelnemer of maak de keuze leeg.
                </p>
            )}
            {draftPersona && draftPersonaId !== savedPersonaId && (
                <p className="share-report-tip">
                    Deze rapportage bevat dan persoonsgegevens van {draftPersona.Name}, zoals naam,
                    aanwezigheid, doelen en antwoorden. Die worden gedeeld met iedereen die de rapportage
                    leest.
                    {!report.ShareCode && ' De rapportage krijgt daarom een toegangscode: alleen wie de link én de code heeft, kan hem openen.'}
                </p>
            )}
            {busy && <p>Bezig met opslaan…</p>}
            {error && <p className="share-report-error">{error}</p>}
        </div>
    );

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            pageHeader="Deelnemer"
            description="Kies over welke deelnemer deze rapportage gaat. Alle widgets tonen dan alleen de gegevens van die deelnemer."
            confirm={save}
            confirmDisabled={!loaded || busy}
            content={content}
        />
    );
};

export default ReportPersonaSelector;
